import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CalendarDate, today, getLocalTimeZone } from "@internationalized/date";
import moment from "moment";
import { fetchSingleAccountSummary, fetchPerformanceReport, fetchPerformanceEntities, refreshClientAccountSummary } from "../../../redux/accountSummarySlice";
export const granularityMap = {
    day: "daily",
    week: "weekly",
    month: "monthly",
};
// Helpers
const formatCalendarDate = (date) => {
    const year = date.year;
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const getDateRangeFromTab = (range) => {
    // UTC, not local time — the backend does all date-range math in UTC, so
    // "today" here must match it or requests can land on a day the backend
    // hasn't rolled over to yet (see AccountSummaryMain.tsx getDateRange).
    const now = moment.utc();
    const todayStr = now.format('YYYY-MM-DD');
    switch (range) {
        case 'today':
            const yesterday = now.clone().subtract(1, 'days');
            return {
                start: todayStr,
                end: todayStr,
                compareStart: yesterday.format('YYYY-MM-DD'),
                compareEnd: yesterday.format('YYYY-MM-DD')
            };
        case 'yesterday':
            const yest = now.clone().subtract(1, 'days');
            const dayBefore = now.clone().subtract(2, 'days');
            return {
                start: yest.format('YYYY-MM-DD'),
                end: yest.format('YYYY-MM-DD'),
                compareStart: dayBefore.format('YYYY-MM-DD'),
                compareEnd: dayBefore.format('YYYY-MM-DD')
            };
        case 'last7days':
            // MTD-1: 7 full days ending yesterday — today is excluded since it's incomplete
            const l7Start = now.clone().subtract(7, 'days');
            const l7End = now.clone().subtract(1, 'days');
            // Match backend's previousSevenDays: subtract(14) to subtract(8) from today
            const prev7Start = now.clone().subtract(14, 'days');
            const prev7End = now.clone().subtract(8, 'days');
            return {
                start: l7Start.format('YYYY-MM-DD'),
                end: l7End.format('YYYY-MM-DD'),
                compareStart: prev7Start.format('YYYY-MM-DD'),
                compareEnd: prev7End.format('YYYY-MM-DD')
            };
        case 'last30days':
            // MTD-1: 30 full days ending yesterday — today is excluded since it's incomplete
            const l30Start = now.clone().subtract(30, 'days');
            const l30End = now.clone().subtract(1, 'days');
            // Match backend's previousThirtyDays: subtract(60) to subtract(31) from today
            const prev30Start = now.clone().subtract(60, 'days');
            const prev30End = now.clone().subtract(31, 'days');
            return {
                start: l30Start.format('YYYY-MM-DD'),
                end: l30End.format('YYYY-MM-DD'),
                compareStart: prev30Start.format('YYYY-MM-DD'),
                compareEnd: prev30End.format('YYYY-MM-DD')
            };
        case 'week': // Week -> Last 7 days vs Same dates in Previous Month (Month-over-Month)
            const wStart = now.clone().subtract(6, 'days');
            const wEnd = now.clone();
            const wCompareStart = wStart.clone().subtract(1, 'month');
            const wCompareEnd = wEnd.clone().subtract(1, 'month');
            return {
                start: wStart.format('YYYY-MM-DD'),
                end: wEnd.format('YYYY-MM-DD'),
                compareStart: wCompareStart.format('YYYY-MM-DD'),
                compareEnd: wCompareEnd.format('YYYY-MM-DD')
            };
        case 'month': // Month -> This/Last Month vs Same dates Last Month
            const todayDate = moment.utc();
            let mStart, mEnd;
            if (todayDate.date() !== 1) {
                mStart = todayDate.clone().startOf('month');
                mEnd = todayDate.clone().subtract(1, 'day');
            }
            else {
                mStart = todayDate.clone().subtract(1, 'month').startOf('month');
                mEnd = todayDate.clone().subtract(1, 'month').endOf('month');
            }
            const mCompareStart = mStart.clone().subtract(1, 'month');
            const mCompareEnd = mEnd.clone().subtract(1, 'month');
            return {
                start: mStart.format('YYYY-MM-DD'),
                end: mEnd.format('YYYY-MM-DD'),
                compareStart: mCompareStart.format('YYYY-MM-DD'),
                compareEnd: mCompareEnd.format('YYYY-MM-DD')
            };
        case 'day': // Day -> Month to Yesterday (or Prev Month if today is 1st)
            const dNow = moment.utc();
            let dStart, dEnd;
            if (dNow.date() !== 1) {
                dStart = dNow.clone().startOf('month');
                dEnd = dNow.clone().subtract(1, 'day');
            }
            else {
                dStart = dNow.clone().subtract(1, 'month').startOf('month');
                dEnd = dNow.clone().subtract(1, 'month').endOf('month');
            }
            const dCompareStart = dStart.clone().subtract(1, 'month');
            const dCompareEnd = dEnd.clone().subtract(1, 'month');
            return {
                start: dStart.format('YYYY-MM-DD'),
                end: dEnd.format('YYYY-MM-DD'),
                compareStart: dCompareStart.format('YYYY-MM-DD'),
                compareEnd: dCompareEnd.format('YYYY-MM-DD')
            };
        default:
            return { start: todayStr, end: todayStr };
    }
};
export const useClientRowState = (data) => {
    const dispatch = useDispatch();
    const [expandedRowState, setExpandedRowState] = useState({});
    // Helper to get defaults or current state
    const getRowState = (rowId, row) => {
        const defaultState = {
            selectedRange: "today",
            startDate: today(getLocalTimeZone()),
            endDate: today(getLocalTimeZone()),
            compareStartDate: null,
            compareEndDate: null,
            activeTab: row?.networks?.[0]?.network ? row.networks[0].network.toLowerCase() : "all channels",
            fetchedData: null,
            isLoadingData: false,
            lastUpdated: null,
            isRefreshingClient: false,
            hasCustomRange: false,
            viewMode: 'account_summary',
            performanceData: null,
            isLoadingPerformance: false,
            selectedGroup: "All",
            selectedEntity: "",
            granularity: "daily"
        };
        return expandedRowState[rowId] || defaultState;
    };
    // Helper to convert YYYY-MM-DD string to CalendarDate
    const toCalendarDate = (dateStr) => {
        if (!dateStr)
            return null;
        const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.split(' ')[0];
        const [year, month, day] = datePart.split(/[-/]/).map(Number);
        return new CalendarDate(year, month, day);
    };
    const getStartEnd = (state) => {
        // ... (rest of function remains same, logic handles state.startDate now being present)
        let start = "";
        let end = "";
        let compareStart = undefined;
        let compareEnd = undefined;
        if (state.startDate && state.endDate) {
            start = formatCalendarDate(state.startDate);
            end = formatCalendarDate(state.endDate);
        }
        else {
            const { start: s, end: e } = getDateRangeFromTab(state.selectedRange || "today");
            start = s;
            end = e;
        }
        if (state.compareStartDate && state.compareEndDate) {
            compareStart = formatCalendarDate(state.compareStartDate);
            compareEnd = formatCalendarDate(state.compareEndDate);
        }
        return { start, end, compareStart, compareEnd };
    };
    const fetchClientData = async (rowId, clientId, startDate, endDate, compareStartDate, compareEndDate, granularity = 'daily', refreshData = false) => {
        setExpandedRowState(prev => ({
            ...prev,
            [rowId]: { ...(prev[rowId] || getRowState(rowId, null)), isLoadingData: true }
        }));
        // 🔥 Compare single vs multi day override
        let finalGranularity = granularity;
        if (compareStartDate && compareEndDate) {
            const mainDays = moment(endDate).diff(moment(startDate), "days") + 1;
            const compareDays = moment(compareEndDate).diff(moment(compareStartDate), "days") + 1;
            // 👉 Main single day + Compare multi day → force daily
            if (mainDays === 1 && compareDays > 1) {
                finalGranularity = "daily";
            }
        }
        try {
            const result = await dispatch(fetchSingleAccountSummary({
                startDate,
                endDate,
                compareStartDate,
                compareEndDate,
                clientId,
                aggregation: finalGranularity,
                comparison: "previous_period",
                refreshData
            })).unwrap();
            const matchedClient = result.data.find((d) => d.client_id === clientId);
            setExpandedRowState(prev => ({
                ...prev,
                [rowId]: {
                    ...prev[rowId],
                    fetchedData: matchedClient || null,
                    lastUpdated: result.last_updated || null,
                    isLoadingData: false
                }
            }));
        }
        catch (error) {
            console.error('Error fetching client data:', error);
            setExpandedRowState(prev => ({
                ...prev,
                [rowId]: { ...prev[rowId], isLoadingData: false }
            }));
        }
    };
    const fetchPerformance = async (rowId, row, state, overrides = {}) => {
        const { start, end, compareStart, compareEnd } = getStartEnd(state);
        const group = overrides.group || state.selectedGroup || "All";
        const entityIds = overrides.entityIds || [];
        const aggregation = overrides.aggregation || state.granularity || "daily";
        setExpandedRowState(prev => ({
            ...prev,
            [rowId]: { ...state, isLoadingPerformance: true }
        }));
        try {
            const res = await dispatch(fetchPerformanceReport({
                client_id: row._id || row.id,
                start_date: start,
                end_date: end,
                group,
                entity_ids: entityIds,
                compare_start_date: compareStart,
                compare_end_date: compareEnd,
                aggregation
            })).unwrap();
            setExpandedRowState(prev => ({
                ...prev,
                [rowId]: {
                    ...prev[rowId],
                    performanceData: res.data,
                    isLoadingPerformance: false,
                    selectedGroup: overrides.group !== undefined ? overrides.group : prev[rowId].selectedGroup,
                    entityList: overrides.entityIds !== undefined ? prev[rowId].entityList : prev[rowId].entityList // bit trickier for logic
                }
            }));
        }
        catch {
            setExpandedRowState(prev => ({
                ...prev,
                [rowId]: { ...prev[rowId], isLoadingPerformance: false }
            }));
        }
    };
    const initializeRowFromGlobal = async (rowId, globalRange) => {
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        if (expandedRowState[rowId]) {
            const currentState = expandedRowState[rowId];
            const { start, end, compareStart, compareEnd } = getStartEnd(currentState);
            if (currentState.viewMode === 'performance_report') {
                await fetchPerformance(rowId, row, currentState, { aggregation: currentState.granularity });
            }
            else {
                await fetchClientData(rowId, row._id || row.id, start, end, compareStart, compareEnd, currentState.granularity);
            }
            return;
        }
        const { start, end, compareStart, compareEnd } = getDateRangeFromTab(globalRange);
        const autoGranularity = calculateGranularity(start, end);
        const currentState = getRowState(rowId, row);
        const newState = {
            ...currentState,
            selectedRange: globalRange,
            startDate: toCalendarDate(start),
            endDate: toCalendarDate(end),
            compareStartDate: toCalendarDate(compareStart),
            compareEndDate: toCalendarDate(compareEnd),
            granularity: autoGranularity,
            hasCustomRange: false,
            isLoadingData: true,
        };
        setExpandedRowState(prev => ({ ...prev, [rowId]: newState }));
        // Fetch data
        await fetchClientData(rowId, row._id || row.id, start, end, compareStart, compareEnd, autoGranularity);
    };
    const handleDateRangeTabChange = async (rowId, range) => {
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const { start, end, compareStart, compareEnd } = getDateRangeFromTab(range);
        const autoGranularity = calculateGranularity(start, end);
        const currentState = getRowState(rowId, row);
        const newState = {
            ...currentState,
            selectedRange: range,
            startDate: toCalendarDate(start),
            endDate: toCalendarDate(end),
            compareStartDate: toCalendarDate(compareStart),
            compareEndDate: toCalendarDate(compareEnd),
            activeTab: "all channels",
            granularity: autoGranularity,
            hasCustomRange: false,
            isLoadingPerformance: currentState.viewMode === 'performance_report'
        };
        setExpandedRowState(prev => ({ ...prev, [rowId]: newState }));
        fetchClientData(rowId, row._id || row.id, start, end, compareStart, compareEnd, autoGranularity);
        if (newState.viewMode === 'performance_report') {
            fetchPerformance(rowId, row, newState, { aggregation: "daily" });
        }
    };
    const calculateGranularity = (startDate, endDate) => {
        const start = moment(startDate);
        const end = moment(endDate);
        const daysDiff = end.diff(start, 'days') + 1;
        if (daysDiff <= 1)
            return 'hourly';
        if (daysDiff <= 14)
            return 'daily';
        if (daysDiff <= 90)
            return 'weekly';
        return 'monthly';
    };
    const handleDateApply = async (rowId, mainRange, compareRange) => {
        if (!mainRange)
            return;
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const start = formatCalendarDate(mainRange.start);
        const end = formatCalendarDate(mainRange.end);
        const compareStart = compareRange?.start ? formatCalendarDate(compareRange.start) : undefined;
        const compareEnd = compareRange?.end ? formatCalendarDate(compareRange.end) : undefined;
        const autoGranularity = calculateGranularity(start, end);
        const currentState = getRowState(rowId, row);
        const newState = {
            ...currentState,
            startDate: mainRange.start,
            endDate: mainRange.end,
            compareStartDate: compareRange?.start || null,
            compareEndDate: compareRange?.end || null,
            granularity: autoGranularity,
            hasCustomRange: true,
            isLoadingPerformance: currentState.viewMode === 'performance_report'
        };
        setExpandedRowState(prev => ({ ...prev, [rowId]: newState }));
        fetchClientData(rowId, row._id || row.id, start, end, compareStart, compareEnd, autoGranularity);
        if (newState.viewMode === 'performance_report') {
            fetchPerformance(rowId, row, newState, { aggregation: "daily" }); // Original logic was daily for PR here too?
        }
    };
    const handleGranularityChange = async (rowId, value) => {
        // Calculate new range based on granularity
        let newRange = "day";
        if (value === 'daily')
            newRange = 'day';
        if (value === 'weekly')
            newRange = 'week';
        if (value === 'monthly')
            newRange = 'month';
        if (rowId === 'all_clients') {
            const rowState = expandedRowState[rowId] || getRowState(rowId, null);
            let start = "", end = "";
            if (rowState.startDate && rowState.endDate) {
                start = formatCalendarDate(rowState.startDate);
                end = formatCalendarDate(rowState.endDate);
            }
            else {
                // Fallback (shouldn't happen if initialized)
                const d = getDateRangeFromTab('day');
                start = d.start;
                end = d.end;
            }
            setExpandedRowState(prev => {
                const current = prev[rowId] || getRowState(rowId, null);
                return {
                    ...prev,
                    [rowId]: {
                        ...current,
                        granularity: value,
                        ...(current.viewMode === 'performance_report' ? { selectedRange: newRange } : {}),
                    }
                };
            });
            await fetchClientData(rowId, 'all_clients', start, end, undefined, undefined, value);
            return;
        }
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const currentState = getRowState(rowId, row);
        // Extract dates as strings for API
        let start = "", end = "";
        let compareStart = undefined;
        let compareEnd = undefined;
        if (currentState.startDate && currentState.endDate) {
            start = formatCalendarDate(currentState.startDate);
            end = formatCalendarDate(currentState.endDate);
        }
        else {
            const d = getDateRangeFromTab(currentState.selectedRange || "today");
            start = d.start;
            end = d.end;
        }
        if (currentState.compareStartDate && currentState.compareEndDate) {
            compareStart = formatCalendarDate(currentState.compareStartDate);
            compareEnd = formatCalendarDate(currentState.compareEndDate);
        }
        setExpandedRowState(prev => {
            const current = prev[rowId] || getRowState(rowId, null);
            return {
                ...prev,
                [rowId]: {
                    ...current,
                    granularity: value,
                    // ⭐ Fix: Only update selectedRange if in Performance Report mode
                    ...(current.viewMode === 'performance_report' ? { selectedRange: newRange } : {}),
                    isLoadingData: currentState.viewMode !== 'performance_report',
                    isLoadingPerformance: currentState.viewMode === 'performance_report'
                }
            };
        });
        // Call appropriate API based on view mode
        if (currentState.viewMode === 'performance_report') {
            // ✅ PRESERVE existing date range and comparison state
            // Don't reset to predefined ranges when just changing granularity
            const newState = {
                ...currentState,
                granularity: value,
                selectedRange: newRange, // Update tab highlight only
                isLoadingPerformance: true
            };
            fetchPerformance(rowId, row, newState, { aggregation: value });
        }
        else {
            fetchClientData(rowId, row._id || row.id, start, end, compareStart, compareEnd, value);
        }
    };
    const handleGroupChange = async (rowId, group) => {
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const currentState = getRowState(rowId, row);
        const entityList = group === "All" ? [] : currentState.entitiesData?.find((g) => g.group_name === group)?.data.map((en) => ({
            id: en.entity_id,
            name: en.entity_name,
            visible: true,
        })) || [];
        const newState = { ...currentState, selectedGroup: group, entityList };
        fetchPerformance(rowId, row, newState, { group });
    };
    const handleEntityToggle = async (rowId, updatedEntities) => {
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const currentState = getRowState(rowId, row);
        const excludedIds = updatedEntities.filter(e => !e.visible).map(e => e.id);
        const newState = { ...currentState, entityList: updatedEntities };
        const { start, end, compareStart, compareEnd } = getStartEnd(currentState);
        setExpandedRowState(prev => ({
            ...prev,
            [rowId]: { ...newState, isLoadingPerformance: true }
        }));
        try {
            const res = await dispatch(fetchPerformanceReport({
                client_id: row._id || row.id,
                start_date: start,
                end_date: end,
                group: currentState.selectedGroup || "All",
                entity_ids: excludedIds,
                compare_start_date: compareStart,
                compare_end_date: compareEnd,
                aggregation: "daily"
            })).unwrap();
            setExpandedRowState(prev => ({
                ...prev,
                [rowId]: {
                    ...prev[rowId],
                    performanceData: res.data,
                    isLoadingPerformance: false
                }
            }));
        }
        catch {
            setExpandedRowState(prev => ({
                ...prev,
                [rowId]: { ...prev[rowId], isLoadingPerformance: false }
            }));
        }
    };
    const toggleViewMode = async (rowId) => {
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const currentState = getRowState(rowId, row);
        if (currentState.viewMode === 'performance_report') {
            // Map Performance ranges back to Account Summary ranges
            let accountRange = currentState.selectedRange;
            if (currentState.selectedRange === 'day')
                accountRange = 'today';
            if (currentState.selectedRange === 'week')
                accountRange = 'last7days';
            if (currentState.selectedRange === 'month')
                accountRange = 'last30days';
            setExpandedRowState(prev => ({
                ...prev,
                [rowId]: { ...currentState, viewMode: 'account_summary', selectedRange: accountRange }
            }));
        }
        else {
            // Switch to PR - Default to Month view
            const { start, end, compareStart, compareEnd } = getDateRangeFromTab('month');
            const newState = {
                ...currentState,
                viewMode: 'performance_report',
                startDate: toCalendarDate(start),
                endDate: toCalendarDate(end),
                compareStartDate: toCalendarDate(compareStart),
                compareEndDate: toCalendarDate(compareEnd),
                selectedRange: 'month',
                granularity: 'monthly',
                isLoadingPerformance: true,
                selectedGroup: 'All',
                selectedEntity: ''
            };
            setExpandedRowState(prev => ({ ...prev, [rowId]: newState }));
            try {
                const reportResult = await dispatch(fetchPerformanceReport({
                    client_id: row._id || row.id,
                    start_date: start,
                    end_date: end,
                    compare_start_date: compareStart,
                    compare_end_date: compareEnd,
                    group: 'All',
                    entity_ids: [],
                    aggregation: 'monthly'
                })).unwrap();
                setExpandedRowState(prev => ({
                    ...prev,
                    [rowId]: {
                        ...prev[rowId],
                        performanceData: reportResult.data,
                        isLoadingPerformance: false
                    }
                }));
            }
            catch (err) {
                setExpandedRowState(prev => ({
                    ...prev,
                    [rowId]: { ...prev[rowId], isLoadingPerformance: false }
                }));
            }
            // Fetch Entities (Leagues/Teams)
            try {
                const entitiesResult = await dispatch(fetchPerformanceEntities({ client_id: row._id || row.id })).unwrap();
                setExpandedRowState(prev => ({
                    ...prev,
                    [rowId]: {
                        ...prev[rowId],
                        entitiesData: entitiesResult.data
                    }
                }));
            }
            catch (err) {
                console.error("Failed to fetch entities", err);
            }
        }
    };
    const syncExpandedRowWithGlobalRange = async (rowId, globalRange) => {
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const currentState = expandedRowState[rowId];
        if (!currentState)
            return;
        const { start, end, compareStart, compareEnd } = getDateRangeFromTab(globalRange);
        const autoGranularity = calculateGranularity(start, end);
        const newState = {
            ...currentState,
            selectedRange: globalRange,
            startDate: toCalendarDate(start),
            endDate: toCalendarDate(end),
            compareStartDate: toCalendarDate(compareStart),
            compareEndDate: toCalendarDate(compareEnd),
            granularity: autoGranularity,
            hasCustomRange: false,
            isLoadingData: currentState.viewMode !== "performance_report",
            isLoadingPerformance: currentState.viewMode === "performance_report",
        };
        setExpandedRowState(prev => ({
            ...prev,
            [rowId]: {
                ...newState,
                fetchedData: null
            }
        }));
        if (currentState.viewMode === "performance_report") {
            await fetchPerformance(rowId, row, newState, { aggregation: autoGranularity });
        }
        else {
            await fetchClientData(rowId, row._id || row.id, start, end, compareStart, compareEnd, autoGranularity);
        }
    };
    // Triggers a live provider resync for this client + its currently selected date
    // range (not the compare range), then re-fetches this row's data bypassing cache
    // so the numbers reflect the freshly-synced data.
    const handleClientRefresh = async (rowId) => {
        const row = data.find(r => (r._id || r.id) === rowId);
        if (!row)
            return;
        const currentState = getRowState(rowId, row);
        const { start, end, compareStart, compareEnd } = getStartEnd(currentState);
        const clientId = row._id || row.id;
        setExpandedRowState(prev => ({
            ...prev,
            [rowId]: { ...(prev[rowId] || currentState), isRefreshingClient: true }
        }));
        try {
            await dispatch(refreshClientAccountSummary({ clientId, startDate: start, endDate: end })).unwrap();
        }
        catch (error) {
            console.error('Client refresh failed:', error);
        }
        await fetchClientData(rowId, clientId, start, end, compareStart, compareEnd, currentState.granularity, true);
        setExpandedRowState(prev => ({
            ...prev,
            [rowId]: { ...(prev[rowId] || currentState), isRefreshingClient: false }
        }));
    };
    const setRowActiveTab = (rowId, tab) => {
        setExpandedRowState(prev => ({
            ...prev,
            [rowId]: { ...prev[rowId], activeTab: tab }
        }));
    };
    return {
        syncExpandedRowWithGlobalRange,
        expandedRowState,
        setExpandedRowState,
        getRowState, // helper
        handleDateRangeTabChange,
        handleDateApply,
        handleGranularityChange,
        handleGroupChange,
        handleEntityToggle,
        toggleViewMode,
        setRowActiveTab,
        initializeRowFromGlobal,
        handleClientRefresh,
    };
};
